import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton
} from '@material-ui/core';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoIcon from '@material-ui/icons/Info';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';

function normalizeStatus(status) {
  if (!status) return 'failed';
  const v = String(status).toLowerCase();
  if (v.includes('sync') || v === 'success' || v === 'ok') return 'synced';
  if (v.includes('skip') || v.includes('exist') || v.includes('already')) return 'skipped';
  if (v.includes('fail') || v.includes('error')) return 'failed';
  return 'failed';
}

function extractIds(r) {
  if (!r) return [];

  const candidates = [r.inventoryIds, r.inventoryId, r.id, r.inventory_id, r.inventoryID];

  for (const c of candidates) {
    if (Array.isArray(c)) return c.map(String);
    if (c !== null && c !== undefined && c !== '') return [String(c)];
  }
  if (typeof r.inventoryIds === 'string') return r.inventoryIds.split(',').map(x => x.trim()).filter(Boolean);
  return [];
}

function groupAndFlatten(results = []) {
  const grouped = { synced: [], skipped: [], failed: [] };

  (results || []).forEach((r) => {
    const status = normalizeStatus(r?.status);
    const ids = extractIds(r) || [];
    if (!ids || ids.length === 0) return;
    grouped[status].push(...ids);
  });

  Object.keys(grouped).forEach(k => grouped[k] = Array.from(new Set(grouped[k])));
  return grouped;
}

// Per-item failure reasons (currently sent by the eBay sync as `failures: [{ inventoryId, reason }]`).
function extractFailureReasons(results = []) {
  const reasons = {};
  (results || []).forEach((r) => {
    (r?.failures || []).forEach((f) => {
      if (f && f.inventoryId !== undefined && f.reason) reasons[String(f.inventoryId)] = f.reason;
    });
  });
  return reasons;
}

function StatusSection({ icon, title, description, ids = [], mapInventoryIdToLabel, reasons = {} }) {
  const validIds = (ids || []).filter(id => id !== null && id !== undefined && String(id).trim() !== '');
  if (validIds.length === 0) return null;
  const label = (id) => mapInventoryIdToLabel ? mapInventoryIdToLabel(id) : id;
  const hasReasons = validIds.some(id => reasons[String(id)]);

  return (
    <Box mb={3}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item>
          <Box mt={0.5}>
            {icon}
          </Box>
        </Grid>

        <Grid item xs>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {title} ({validIds.length})
          </Typography>

          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>

          {hasReasons ? (
            validIds.map(id => (
              <Typography key={id} variant="body2" style={{ marginTop: 6, color: '#000' }}>
                <strong>{label(id)}</strong>
                {reasons[String(id)] ? ` - ${reasons[String(id)]}` : ''}
              </Typography>
            ))
          ) : (
            <Typography
              variant="body2"
              style={{ marginTop: 6, color: '#000' }}
            >
              {validIds.map(label).join(', ')}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ShopifySyncResultDialog({ open, onClose, results = [], onRetryFailed, mapInventoryIdToLabel, platformName = 'Shopify' }) {
  const grouped = groupAndFlatten(results);
  const failureReasons = extractFailureReasons(results);

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {platformName} Inventory Sync Summary
        <IconButton onClick={onClose} style={{ float: 'right' }} className="noprint">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <StatusSection
          icon={<CheckCircleIcon style={{ color: '#2e7d32' }} />}
          title="Successfully Synced"
          description={`These inventories were successfully synced to ${platformName}.`}
          ids={grouped.synced}
          mapInventoryIdToLabel={mapInventoryIdToLabel}
        />

        <StatusSection
          icon={<InfoIcon style={{ color: '#f57c00' }} />}
          title="Skipped"
          description={`These inventories already exist in ${platformName}, so they were skipped.`}
          ids={grouped.skipped}
          mapInventoryIdToLabel={mapInventoryIdToLabel}
        />

        <StatusSection
          icon={<ErrorIcon style={{ color: '#d32f2f' }} />}
          title="Failed"
          description="These inventories could not be synced. Please try again or contact support."
          ids={grouped.failed}
          mapInventoryIdToLabel={mapInventoryIdToLabel}
          reasons={failureReasons}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">Close</Button>
        {(grouped.failed && grouped.failed.length > 0) && (
          <Button color="secondary" variant="contained" onClick={() => onRetryFailed && onRetryFailed(grouped.failed)}>
            Retry Failed ({grouped.failed.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
